/**
 * Autosave d'un document d'éditeur (visuels, templates) — remplace le bouton Enregistrer
 * hors panel. Calibré pour des documents légers (p99 ~10 Ko, max observé 37 Ko) :
 *  - debounce trailing (500 ms) : un PATCH par accalmie, pas par mutation ;
 *  - dirty-check : rien ne part si le JSON sérialisé n'a pas changé ;
 *  - flush de sécurité (20 s) pendant une activité continue ;
 *  - flush à la fermeture (pagehide/visibilitychange, fetch keepalive) ;
 *  - flush-on-interaction : toute requête Turbo (navigation de frame, soumission de
 *    formulaire) part avec un document déjà en base — le serveur peut agir sur l'état
 *    courant (import de composition, naissance du document…). Le dirty-check rend le réflexe gratuit.
 *  - la toolbox ATTEND la sauvegarde : ses sous-pages rendent le calque depuis le document
 *    en base, la requête du frame est suspendue (Turbo resume) jusqu'au retour du PATCH.
 *
 * onState reçoit "saved" | "saving" | "error" pour la pastille d'état : « saving » dès la
 * première mutation (pas au départ du PATCH, qui ne dure que quelques dizaines de ms) et
 * tenu au moins minSaving ms, pour qu'on comprenne qu'il se passe quelque chose. onSaved est
 * appelé après chaque PATCH réussi — les suiveurs du document sauvegardé (le panneau ouvert
 * de la toolbox) se rechargent là, et seulement là.
 */
export class Autosave {
  /** Le frame dont les requêtes attendent la sauvegarde. */
  static AWAITING_FRAME_ID = "toolbox-content";

  constructor({ url, getPayload, onState, onSaved, debounce = 500, maxInterval = 20000, minSaving = 1000 }) {
    this.url = url;
    this.getPayload = getPayload;
    this.onState = onState || (() => {});
    this.onSaved = onSaved || (() => {});
    this.debounceMs = debounce;
    this.maxIntervalMs = maxInterval;
    this.minSavingMs = minSaving;
    this._lastSaved = null;
    this._inflightPromise = null;
    this._state = "saved";
    this._savingSince = 0;

    this._onPageHide = () => this.flush({ keepalive: true });
    window.addEventListener("pagehide", this._onPageHide);
    document.addEventListener("visibilitychange", this._onVisibility = () => {
      if (document.visibilityState === "hidden") this.flush({ keepalive: true });
    });
    document.addEventListener("turbo:before-fetch-request", this._onTurboRequest = (e) => this._beforeTurboRequest(e));
  }

  /** Une requête du frame toolbox part une fois le document en base ; les autres partent
   *  tout de suite, la sauvegarde en parallèle. */
  _beforeTurboRequest(event) {
    if (event.target?.id !== Autosave.AWAITING_FRAME_ID || typeof event.detail?.resume !== "function") {
      this.flush();
      return;
    }
    event.preventDefault();
    this.settle().finally(() => event.detail.resume());
  }

  destroy() {
    clearTimeout(this._debounceTimer);
    clearTimeout(this._maxTimer);
    clearTimeout(this._retryTimer);
    clearTimeout(this._settleStateTimer);
    window.removeEventListener("pagehide", this._onPageHide);
    document.removeEventListener("visibilitychange", this._onVisibility);
    document.removeEventListener("turbo:before-fetch-request", this._onTurboRequest);
  }

  /** À appeler après chaque mutation du document. */
  schedule() {
    this._setState("saving");
    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this.flush(), this.debounceMs);
    // Borne la perte maximale pendant une frénésie ininterrompue.
    if (!this._maxTimer) {
      this._maxTimer = setTimeout(() => this.flush(), this.maxIntervalMs);
    }
  }

  /** Attend que le document en base soit à jour : un save en vol se termine, puis ce qui a
   *  changé entre-temps part. Une sauvegarde en erreur ne bloque pas (elle réessaie seule). */
  async settle() {
    if (this._inflightPromise) await this._inflightPromise;
    return this.flush();
  }

  /** Prime le dirty-check avec l'état initial (rien à sauver tant qu'on n'a pas touché). */
  prime() {
    this._lastSaved = this.getPayload();
  }

  async flush({ keepalive = false } = {}) {
    clearTimeout(this._debounceTimer);
    clearTimeout(this._maxTimer);
    this._debounceTimer = null;
    this._maxTimer = null;

    const payload = this.getPayload();
    if (payload === this._lastSaved) {
      // Mutation blanche (même JSON) : la pastille annonçait un enregistrement, on la rend.
      if (!this._inflightPromise) this._setState("saved");
      return true;
    }
    if (this._inflightPromise && !keepalive) {
      // Un save est en vol : on repassera — le debounce relancé garantit le rattrapage.
      this.schedule();
      return false;
    }

    this._inflightPromise = this._save(payload, keepalive);
    try {
      return await this._inflightPromise;
    } finally {
      this._inflightPromise = null;
    }
  }

  /** « saving » tout de suite ; « saved » attend que « saving » ait été lisible (minSaving) ;
   *  « error » tout de suite. Un nouveau « saving » annule un « saved » en attente. */
  _setState(state) {
    clearTimeout(this._settleStateTimer);
    if (state === "saving") {
      if (this._state !== "saving") this._savingSince = Date.now();
    } else if (state === "saved" && this._state === "saving") {
      // Une mutation attend son flush : ce « saved » serait un faux calme, le flush en posera un.
      if (this._debounceTimer) return;
      const remaining = this._savingSince + this.minSavingMs - Date.now();
      if (remaining > 0) {
        this._settleStateTimer = setTimeout(() => this._setState("saved"), remaining);
        return;
      }
    }
    if (this._state === state) return;
    this._state = state;
    this.onState(state);
  }

  async _save(payload, keepalive) {
    this._setState("saving");
    try {
      const response = await fetch(this.url, {
        method: "PATCH",
        keepalive,
        headers: {
          // Le document part tel quel : un formulaire percent-encode toute la ponctuation
          // du JSON (le corps triplait), et keepalive plafonne à 64 Ko en vol.
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
        },
        body: payload,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      this._lastSaved = payload;
      this._setState("saved");
      this.onSaved();
      return true;
    } catch {
      this._setState("error");
      clearTimeout(this._retryTimer);
      this._retryTimer = setTimeout(() => this.flush(), 5000);
      return false;
    }
  }
}
